import { Compass, ShieldCheck, Sparkles, Users } from "lucide-react";
import { Reveal, TriangleMark } from "./Weave";

const PORTRAIT_IMG = "/photos/cohort-together.webp";

const OUTCOMES = [
  { icon: Sparkles, label: "Courage that comes from confidence" },
  { icon: ShieldCheck, label: "A safe space for trial and error" },
  { icon: Compass, label: "Personal clarity and a strong network" },
  { icon: Users, label: "Mentors and resources on tap" },
];

export default function WhyWeave() {
  return (
    <section id="why" data-testid="outcomes-section" className="scroll-mt-24 bg-ink text-paper">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8 lg:py-32">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative mx-auto max-w-sm">
                <div
                  className="absolute -inset-8 rounded-full opacity-60 blur-3xl"
                  style={{ background: "radial-gradient(circle, rgba(74,160,164,0.5) 0%, rgba(244,184,71,0.18) 55%, transparent 75%)" }}
                  aria-hidden="true"
                />
                <img
                  src={PORTRAIT_IMG}
                  alt="An experienced professional pausing in thought"
                  loading="lazy"
                  className="relative aspect-[4/5] w-full rotate-2 rounded-3xl object-cover ring-1 ring-paper/25"
                />
                <TriangleMark className="absolute -right-7 -top-7 h-14 opacity-95" strokeWidth={5} />
                <p className="relative mt-5 text-sm font-medium italic text-paper/70">
                  Your people — honest conversations, trusted networks.
                </p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-wyellow">Why The Weave</p>
              <h2 className="max-w-2xl text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                We&rsquo;ve been in the room. We understand the pressure. We can help you move forward.
              </h2>
            </Reveal>

            <Reveal delay={0.1}>
              <p className="mt-8 text-2xl font-extrabold tracking-tight text-wyellow sm:text-3xl">
                You don&rsquo;t have to build alone.
              </p>
              <p className="mt-5 max-w-2xl text-lg leading-relaxed text-paper/75">
                Years of experience in the field of reinvention — at your service. The Weave is a
                community, not a classroom: honest conversations, trusted networks, and support that
                grows with you.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <blockquote className="mt-10 border-l-4 border-tealb pl-6">
                <p className="text-lg italic leading-relaxed text-paper/90 sm:text-xl">
                  &ldquo;Faith is taking the first step even when you don&rsquo;t see the whole
                  staircase.&rdquo;
                </p>
                <cite className="mt-3 block text-sm font-semibold not-italic uppercase tracking-[0.15em] text-paper/60">
                  Martin Luther King, Jr
                </cite>
              </blockquote>
            </Reveal>

            <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {OUTCOMES.map((o, i) => (
                <Reveal key={o.label} delay={0.1 + i * 0.07} y={16}>
                  <div className="flex items-center gap-4 rounded-2xl border border-paper/15 bg-paper/5 px-5 py-4 transition-colors duration-300 hover:border-wyellow/50 hover:bg-paper/10">
                    <o.icon className="h-5 w-5 shrink-0 text-wyellow" />
                    <p className="text-sm font-medium leading-snug text-paper sm:text-base">{o.label}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
